if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      getEmbroideryBuilder() {
        const productInfo = this.closest('product-info');
        return productInfo?.querySelector('build-your-bandana') || document.querySelector('build-your-bandana');
      }

      createEmbroideryPairId() {
        if (window.crypto?.randomUUID) return `emb-${window.crypto.randomUUID()}`;
        return `emb-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
      }

      validateEmbroiderySelection() {
        const embroidery = this.getEmbroideryBuilder();
        if (!embroidery) return true;

        const yesInput = embroidery.querySelector('#byb-embroidery-yes');
        if (!yesInput?.checked) return true;

        const letteringInput = embroidery.querySelector('[data-lettering-input]:checked:not(:disabled)');
        const threadInput = embroidery.querySelector('[data-thread-input]:checked:not(:disabled)');
        const dogNameInput = embroidery.querySelector('#byb-dog-name:not(:disabled)');
        const dogName = dogNameInput?.value?.trim() || '';
        const error = embroidery.querySelector('[data-embroidery-error]');
        const missing = [];

        if (!letteringInput) missing.push('lettering style');
        if (!threadInput) missing.push('thread colour');
        if (!dogName) missing.push('name or text');

        if (missing.length === 0) {
          if (error) {
            error.hidden = true;
            error.textContent = '';
          }
          return true;
        }

        if (error) {
          error.textContent = `Please complete your embroidery options: ${missing.join(', ')}.`;
          error.hidden = false;
          error.setAttribute('aria-live', 'assertive');
        }

        const letteringContainer = embroidery.querySelector('[data-lettering-container]');
        const threadContainer = embroidery.querySelector('[data-thread-container]');
        const nameContainer = embroidery.querySelector('[data-name-container]');

        [letteringContainer, threadContainer, nameContainer].forEach((section) => {
          if (!section) return;
          section.classList.add('active');
          section.style.setProperty('--max-height', `${section.scrollHeight}px`);
        });

        if (error) {
          error.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          embroidery.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }

        return false;
      }

      syncEmbroideryProperties(formData) {
        const embroidery = this.getEmbroideryBuilder();
        const addonInput = embroidery?.querySelector('#byb-embroidery-yes:checked');
        this.embroideryPairId = null;
        if (!addonInput) return;

        const letteringInput = embroidery.querySelector('[data-lettering-input]:checked:not(:disabled)');
        const threadInput = embroidery.querySelector('[data-thread-input]:checked:not(:disabled)');
        const dogNameInput = embroidery.querySelector('#byb-dog-name:not(:disabled)');
        const pairId = this.createEmbroideryPairId();
        this.embroideryPairId = pairId;

        formData.set('properties[Embroidery]', 'Yes');
        formData.set('properties[Lettering Style]', letteringInput.value.trim());
        formData.set('properties[Thread Color]', threadInput.value.trim());
        formData.set('properties[Dog name]', dogNameInput.value.trim());
        formData.set('properties[_embroidery_pair]', pairId);
      }

      async handleAddons(formData) {
        const embroidery = this.getEmbroideryBuilder();
        const addonInput = embroidery?.querySelector('#byb-embroidery-yes:checked');
        if (!addonInput) return;

        const pairId = this.embroideryPairId || this.createEmbroideryPairId();
        this.embroideryPairId = pairId;

        // Add the separate £10 embroidery product only after all required
        // personalisation choices have passed validation. Hidden properties link
        // it to the personalised item so cart removals stay in sync.
        formData.append('items[][id]', addonInput.value);
        formData.append('items[][quantity]', 1);
        formData.append('items[][properties][_embroidery_pair]', pairId);
        formData.append('items[][properties][_embroidery_addon]', 'true');
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
        if (!this.validateEmbroiderySelection()) return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        this.syncEmbroideryProperties(formData);

        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }

        await this.handleAddons(formData);

        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: formData.get('id'),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure('add:paint-updated-sections', () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure('add:paint-updated-sections', () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent('add:user-action', evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
